import { Chess, Move, Square as ChessJSSquare } from 'chess.js';
import { Square, PieceType, PieceColor, MoveDetail, CapturedPiecesState } from './types.ts';

const PIECE_VALUES: Record<PieceType, number> = {
  p: 1,
  n: 3,
  b: 3,
  r: 5,
  q: 9,
  k: 0
};

export class ChessGame {
  private engine: Chess;
  private moveHistory: MoveDetail[] = [];

  constructor(fen?: string) {
    this.engine = new Chess(fen);
  }

  public reset(): void {
    this.engine.reset();
    this.moveHistory = [];
  }

  public load(fen: string): boolean {
    try {
      this.engine.load(fen);
      this.moveHistory = [];
      return true;
    } catch {
      return false;
    }
  }

  public get fen(): string {
    return this.engine.fen();
  }

  public get turn(): PieceColor {
    return this.engine.turn() as PieceColor;
  }

  public get isGameOver(): boolean {
    return this.engine.isGameOver();
  }

  public get inCheck(): boolean {
    return this.engine.inCheck();
  }

  public get isCheckmate(): boolean {
    return this.engine.isCheckmate();
  }

  public get isStalemate(): boolean {
    return this.engine.isStalemate();
  }

  public get isDraw(): boolean {
    return this.engine.isDraw();
  }

  public get isThreefoldRepetition(): boolean {
    return this.engine.isThreefoldRepetition();
  }

  public get isInsufficientMaterial(): boolean {
    return this.engine.isInsufficientMaterial();
  }

  public getPiece(square: Square): { type: PieceType; color: PieceColor } | null {
    const p = this.engine.get(square as ChessJSSquare);
    if (!p) return null;
    return { type: p.type as PieceType, color: p.color as PieceColor };
  }

  public getKingSquare(color: PieceColor): Square | null {
    const board = this.engine.board();
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const piece = board[r][c];
        if (piece && piece.type === 'k' && piece.color === color) {
          return piece.square as Square;
        }
      }
    }
    return null;
  }

  public getBoard(): ({ type: PieceType; color: PieceColor; square: Square } | null)[][] {
    const raw = this.engine.board();
    return raw.map((row) =>
      row.map((sq) =>
        sq ? { type: sq.type as PieceType, color: sq.color as PieceColor, square: sq.square as Square } : null
      )
    );
  }

  public getLegalMoves(fromSquare?: Square): { from: Square; to: Square; promotion?: PieceType; captured?: PieceType }[] {
    const moves = this.engine.moves({
      square: fromSquare as ChessJSSquare,
      verbose: true
    }) as Move[];

    return moves.map((m) => ({
      from: m.from as Square,
      to: m.to as Square,
      promotion: m.promotion as PieceType | undefined,
      captured: m.captured as PieceType | undefined
    }));
  }

  public isPromotionMove(from: Square, to: Square): boolean {
    const piece = this.getPiece(from);
    if (!piece || piece.type !== 'p') return false;
    const toRank = to[1];
    return (piece.color === 'w' && toRank === '8') || (piece.color === 'b' && toRank === '1');
  }

  public makeMove(from: Square, to: Square, promotion: PieceType = 'q'): MoveDetail | null {
    try {
      const pieceBefore = this.getPiece(from);
      if (!pieceBefore) return null;

      const isPromo = this.isPromotionMove(from, to);
      const moveOptions: { from: ChessJSSquare; to: ChessJSSquare; promotion?: string } = {
        from: from as ChessJSSquare,
        to: to as ChessJSSquare
      };

      if (isPromo) {
        moveOptions.promotion = promotion;
      }

      // Pre-check for en passant target
      const isEnPassant = pieceBefore.type === 'p' && from[0] !== to[0] && !this.getPiece(to);
      let capturedSquare: Square | undefined = undefined;
      let capturedColor: PieceColor | undefined = undefined;
      let capturedPieceType: PieceType | undefined = undefined;

      if (isEnPassant) {
        capturedSquare = `${to[0]}${from[1]}` as Square;
        const cap = this.getPiece(capturedSquare);
        if (cap) {
          capturedPieceType = cap.type;
          capturedColor = cap.color;
        }
      } else {
        const directCap = this.getPiece(to);
        if (directCap) {
          capturedSquare = to;
          capturedPieceType = directCap.type;
          capturedColor = directCap.color;
        }
      }

      const moveObj = this.engine.move(moveOptions);
      if (!moveObj) return null;

      const isCastling = moveObj.flags.includes('k') || moveObj.flags.includes('q');

      const detail: MoveDetail = {
        from: moveObj.from as Square,
        to: moveObj.to as Square,
        promotion: moveObj.promotion as PieceType | undefined,
        captured: capturedPieceType || (moveObj.captured as PieceType | undefined),
        capturedColor: capturedColor || (capturedPieceType ? (pieceBefore.color === 'w' ? 'b' : 'w') : undefined),
        capturedSquare,
        piece: moveObj.piece as PieceType,
        color: moveObj.color as PieceColor,
        flags: moveObj.flags,
        san: moveObj.san,
        isCheck: this.engine.inCheck(),
        isCheckmate: this.engine.isCheckmate(),
        isDraw: this.engine.isDraw(),
        isCastling,
        isEnPassant,
        fenAfter: this.engine.fen()
      };

      this.moveHistory.push(detail);
      return detail;
    } catch {
      return null;
    }
  }

  public undoMove(): MoveDetail | null {
    const undone = this.engine.undo();
    if (!undone) return null;
    return this.moveHistory.pop() || null;
  }

  public getHistory(): MoveDetail[] {
    return [...this.moveHistory];
  }

  public getCapturedPieces(): CapturedPiecesState {
    const initialCounts: Record<PieceType, number> = {
      p: 8,
      r: 2,
      n: 2,
      b: 2,
      q: 1,
      k: 1
    };

    const currentCounts = {
      w: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 } as Record<PieceType, number>,
      b: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 } as Record<PieceType, number>
    };

    const board = this.engine.board();
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const p = board[r][c];
        if (p) {
          const color = p.color as PieceColor;
          const type = p.type as PieceType;
          currentCounts[color][type]++;
        }
      }
    }

    const capturedW: PieceType[] = []; // white pieces captured
    const capturedB: PieceType[] = []; // black pieces captured

    let whiteMaterial = 0;
    let blackMaterial = 0;

    for (const [typeStr, count] of Object.entries(initialCounts)) {
      const type = typeStr as PieceType;
      const wMissing = Math.max(0, count - currentCounts.w[type]);
      const bMissing = Math.max(0, count - currentCounts.b[type]);

      for (let i = 0; i < wMissing; i++) capturedW.push(type);
      for (let i = 0; i < bMissing; i++) capturedB.push(type);

      whiteMaterial += currentCounts.w[type] * PIECE_VALUES[type];
      blackMaterial += currentCounts.b[type] * PIECE_VALUES[type];
    }

    return {
      w: capturedW,
      b: capturedB,
      scoreAdvantage: whiteMaterial - blackMaterial
    };
  }
}
